<template>
  <v-card class="pa-3 ma-2" outlined>
    <v-row>
      <v-col cols="12">
        <div class="d-flex align-center mb-2">
          <v-icon left color="primary">mdi-hospital-building</v-icon>
          <h3 class="text-h6 font-weight-bold">{{ clinicName }}</h3>
        </div>

        <div class="clinic-info">
          <div v-if="content.address" class="mb-1">
            <v-icon small left>mdi-map-marker</v-icon>
            <span class="text-body-2">{{ clinicAddress }}</span>
          </div>

          <div v-if="content.phone" class="mb-1">
            <v-icon small left>mdi-phone</v-icon>
            <span class="text-body-2">{{ content.phone }}</span>
            <span v-if="content.phone2" class="ml-2">/ {{ content.phone2 }}</span>
          </div>

          <div v-if="content.serviceProducts && content.serviceProducts.length" class="mb-1">
            <v-icon small left>mdi-medical-bag</v-icon>
            <span class="text-body-2"
              >{{ content.serviceProducts.length }} service(s) available</span
            >
          </div>

          <div class="mb-1">
            <v-icon small left>mdi-account-group</v-icon>
            <span class="text-body-2">Staff: {{ content.staffCount || 0 }}</span>
            <v-icon small left class="ml-3">mdi-calendar-check</v-icon>
            <span class="text-body-2">Today's Bookings: {{ content.bookingCount || 0 }}</span>
          </div>

          <div v-if="content.openHours && content.openHours.length" class="mb-1">
            <v-icon small left>mdi-clock-outline</v-icon>
            <span class="text-body-2">{{ formatOpenHoursCompact }}</span>
          </div>

          <div v-if="content.paymentMethods" class="mb-1">
            <v-icon small left>mdi-cash-multiple</v-icon>
            <span class="text-body-2">{{ content.paymentMethods }}</span>
          </div>
        </div>

        <v-row class="mt-3">
          <v-col cols="6">
            <span class="text-caption text--secondary">Created:</span>
            <span class="text-caption ml-1">{{ content.created_at | moment('DD/MM/YYYY') }}</span>
          </v-col>
          <v-col cols="6">
            <span class="text-caption text--secondary">Updated:</span>
            <span class="text-caption ml-1">{{ content.updated_at | moment('DD/MM/YYYY') }}</span>
          </v-col>
        </v-row>
      </v-col>
    </v-row>
  </v-card>
</template>

<script>
export default {
  name: 'ClinicProfileCard',
  props: {
    content: {
      type: Object,
      required: true,
    },
  },
  computed: {
    clinicName() {
      return this.getLocalizedField(this.content.name) || 'Unnamed Clinic';
    },
    clinicAddress() {
      return this.getLocalizedField(this.content.address) || 'No address';
    },
    formatOpenHoursCompact() {
      const openHours = this.content.openHours;
      if (!openHours || !openHours.length) return 'Hours not set';

      const dayMap = {
        1: 'Mon',
        2: 'Tue',
        3: 'Wed',
        4: 'Thu',
        5: 'Fri',
        6: 'Sat',
        7: 'Sun',
      };

      const formatTime = minutes => {
        const hours = Math.floor(minutes / 60);
        const mins = minutes % 60;
        return `${hours}:${mins.toString().padStart(2, '0')}`;
      };

      // Group consecutive days with same hours
      const groups = [];
      let currentGroup = null;

      openHours.forEach(hour => {
        const timeStr = `${formatTime(hour.start)}-${formatTime(hour.end)}`;

        if (!currentGroup || currentGroup.time !== timeStr) {
          currentGroup = {
            days: [hour.isoDayOfWeek],
            time: timeStr,
          };
          groups.push(currentGroup);
        } else if (currentGroup.days[currentGroup.days.length - 1] === hour.isoDayOfWeek - 1) {
          currentGroup.days.push(hour.isoDayOfWeek);
        } else {
          currentGroup = {
            days: [hour.isoDayOfWeek],
            time: timeStr,
          };
          groups.push(currentGroup);
        }
      });

      // Format groups
      const formatted = groups.slice(0, 2).map(group => {
        if (group.days.length === 1) {
          return `${dayMap[group.days[0]]} ${group.time}`;
        } else if (group.days.length === 2) {
          return `${dayMap[group.days[0]]},${dayMap[group.days[1]]} ${group.time}`;
        } else {
          return `${dayMap[group.days[0]]}-${dayMap[group.days[group.days.length - 1]]} ${
            group.time
          }`;
        }
      });

      return formatted.join(' | ') + (groups.length > 2 ? '...' : '');
    },
  },
  methods: {
    getLocalizedField(field) {
      if (!field) return null;
      try {
        const parsed = typeof field === 'string' ? JSON.parse(field) : field;
        return parsed.en || parsed.zh || field;
      } catch (e) {
        return field;
      }
    },
  },
};
</script>

<style scoped>
.clinic-info {
  margin-top: 10px;
}

.clinic-info > div {
  display: flex;
  align-items: center;
}
</style>
